import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const NotificationContext = createContext(null);

// Sound generator using Web Audio API
const playSound = (type) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    gain.gain.value = 0.15;

    if (type === 'success') {
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } else if (type === 'warning') {
      osc.frequency.value = 660;
      osc.type = 'triangle';
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.4);
    } else if (type === 'error') {
      osc.frequency.value = 440;
      osc.type = 'sawtooth';
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.5);
    } else {
      osc.frequency.value = 720;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.2);
    }
  } catch (e) { /* Audio not available */ }
};

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const prevCountRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get('/notifications/in-app', { params: { limit: 20 } }),
        api.get('/notifications/unread-count')
      ]);
      setNotifications(notifRes.data);
      const newCount = countRes.data.count;
      // Play sound if new notifications arrived
      if (newCount > prevCountRef.current && prevCountRef.current > 0 && soundEnabled) {
        const latest = notifRes.data[0];
        if (latest) {
          const type = latest.type?.includes('approved') || latest.type?.includes('success') ? 'success'
            : latest.type?.includes('rejected') || latest.type?.includes('error') ? 'error'
            : latest.type?.includes('request') ? 'warning' : 'info';
          playSound(type);
        }
      }
      prevCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch { /* silently fail */ }
  }, [user, soundEnabled]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Load sound preference
  useEffect(() => {
    if (!user) return;
    api.get('/notification-settings').then(res => {
      setSoundEnabled(res.data?.sound_enabled !== false);
    }).catch(() => {});
  }, [user]);

  const markAsRead = async (id) => {
    await api.put(`/notifications/in-app/${id}/read`);
    fetchNotifications();
  };

  const markAllRead = async () => {
    await api.put('/notifications/in-app/read-all');
    fetchNotifications();
  };

  const value = useMemo(
    () => ({ notifications, unreadCount, markAsRead, markAllRead, fetchNotifications, soundEnabled, setSoundEnabled, playSound }),
    [notifications, unreadCount, fetchNotifications, soundEnabled]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be inside NotificationProvider');
  return ctx;
}
